<template>
  <div class="ftp-dash">
    <Navigation />
    <v-main class="dash-main">
      <div class="dash-shell">

        <!-- Terminal Header -->
        <div class="term-header">
          <div class="term-bar">
            <div class="term-dots">
              <span class="dot dot--red"></span>
              <span class="dot dot--yellow"></span>
              <span class="dot dot--green"></span>
            </div>
            <span class="term-title">ftp-monitor@w-platform ~ /dashboard</span>
            <div class="term-clock">
              <span class="material-symbols-outlined term-clock-icon">schedule</span>
              {{ currentTime }}
            </div>
          </div>
          <div class="term-prompt">
            <div class="prompt-left">
              <span class="prompt-user">root</span><span class="prompt-at">@</span><span class="prompt-host">ftp-ctrl</span>
              <span class="prompt-sep">:</span>
              <span class="prompt-path">~/servers</span>
              <span class="prompt-dollar">$</span>
              <span class="prompt-cmd">status --all --watch</span>
              <span class="cursor-blink">_</span>
            </div>
            <div class="prompt-right">
              <span class="refresh-indicator" :class="{ 'is-refreshing': refreshing }">
                <span class="material-symbols-outlined">sync</span>
              </span>
              <span class="refresh-label">{{ refreshing ? 'SYNCING' : 'LIVE' }}</span>
              <span class="refresh-dot" :class="{ 'is-refreshing': refreshing }"></span>
            </div>
          </div>
        </div>

        <!-- Metrics Strip -->
        <div class="metrics-strip">
          <div class="metric" @click="setFilter(null)" :class="{ active: activeFilter === null }">
            <span class="metric-val">{{ totalCount }}</span>
            <span class="metric-label">TOTAL</span>
            <div class="metric-bar metric-bar--total"></div>
          </div>
          <div class="metric-divider"></div>
          <div class="metric" @click="setFilter('online')" :class="{ active: activeFilter === 'online' }">
            <span class="metric-val metric-val--online">{{ onlineCount }}</span>
            <span class="metric-label">ONLINE</span>
            <div class="metric-bar metric-bar--online" :style="{ width: onlinePct + '%' }"></div>
          </div>
          <div class="metric-divider"></div>
          <div class="metric" @click="setFilter('offline')" :class="{ active: activeFilter === 'offline' }">
            <span class="metric-val metric-val--offline">{{ offlineCount }}</span>
            <span class="metric-label">OFFLINE</span>
            <div class="metric-bar metric-bar--offline" :style="{ width: offlinePct + '%' }"></div>
          </div>
          <div class="metric-divider"></div>
          <div class="metric" @click="setFilter('error')" :class="{ active: activeFilter === 'error' }">
            <span class="metric-val metric-val--error">{{ errorCount }}</span>
            <span class="metric-label">ERROR</span>
            <div class="metric-bar metric-bar--error" :style="{ width: errorPct + '%' }"></div>
          </div>
        </div>

        <!-- Search & Controls -->
        <div class="controls-row">
          <div class="search-box">
            <span class="material-symbols-outlined search-icon">search</span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Filter by client, site or ID..."
              class="search-input"
            />
            <span
              v-if="searchQuery"
              class="material-symbols-outlined search-clear"
              @click="searchQuery = ''"
            >close</span>
          </div>
          <div class="controls-right">
            <button class="ctrl-btn" :class="{ active: viewMode === 'table' }" @click="viewMode = 'table'">
              <span class="material-symbols-outlined">view_list</span>
            </button>
            <button class="ctrl-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
              <span class="material-symbols-outlined">grid_view</span>
            </button>
            <div class="ctrl-divider"></div>
            <button class="ctrl-btn" @click="fetchServers" :disabled="refreshing">
              <span class="material-symbols-outlined" :class="{ 'spin': refreshing }">refresh</span>
            </button>
          </div>
        </div>

        <!-- Table View -->
        <div v-if="viewMode === 'table'" class="table-wrap">
          <table class="srv-table">
            <thead>
              <tr>
                <th class="th-status">STATUS</th>
                <th class="th-id">ID</th>
                <th class="th-client">CLIENT</th>
                <th class="th-site">SITE</th>
                <th class="th-host">HOST</th>
                <th class="th-checked">LAST CHECK</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filteredServers.length && !loading" class="row-empty">
                <td colspan="6">
                  <div class="empty-state">
                    <span class="material-symbols-outlined">dns_off</span>
                    <span>No servers match current filters</span>
                  </div>
                </td>
              </tr>
              <tr
                v-for="s in filteredServers"
                :key="s.id"
                class="srv-row"
                :class="'row--' + s.status.toLowerCase()"
              >
                <td class="td-status">
                  <span class="status-dot" :class="'dot--' + s.status.toLowerCase()"></span>
                  <span class="status-text" :class="'st--' + s.status.toLowerCase()">{{ statusLabel(s.status) }}</span>
                </td>
                <td class="td-id"><code>#{{ s.id }}</code></td>
                <td class="td-client">{{ s.client_name || '---' }}</td>
                <td class="td-site">{{ s.site_name }}</td>
                <td class="td-host"><code>{{ s.ftp_host || '---' }}</code></td>
                <td class="td-checked">
                  <span class="material-symbols-outlined check-icon">schedule</span>
                  {{ relativeTime(s.checked_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Grid View -->
        <div v-if="viewMode === 'grid'" class="grid-wrap">
          <div v-if="!filteredServers.length && !loading" class="empty-state grid-empty">
            <span class="material-symbols-outlined">dns_off</span>
            <span>No servers match current filters</span>
          </div>
          <div
            v-for="s in filteredServers"
            :key="s.id"
            class="srv-card"
            :class="'card--' + s.status.toLowerCase()"
          >
            <div class="card-top">
              <span class="status-dot" :class="'dot--' + s.status.toLowerCase()"></span>
              <span class="status-text" :class="'st--' + s.status.toLowerCase()">{{ statusLabel(s.status) }}</span>
              <code class="card-id">#{{ s.id }}</code>
            </div>
            <div class="card-body">
              <div class="card-client">{{ s.client_name || 'No Client' }}</div>
              <div class="card-site">{{ s.site_name }}</div>
            </div>
            <div class="card-footer">
              <div class="card-host" v-if="s.ftp_host">
                <span class="material-symbols-outlined">link</span>
                <code>{{ s.ftp_host }}</code>
              </div>
              <div class="card-time">
                <span class="material-symbols-outlined">schedule</span>
                {{ relativeTime(s.checked_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="dash-footer">
          <span class="footer-left">
            <span class="material-symbols-outlined">info</span>
            Auto-refresh every 30s &middot; {{ filteredServers.length }} of {{ totalCount }} servers shown
          </span>
          <span class="footer-right">
            Last sync: {{ lastRefresh || '---' }}
          </span>
        </div>

      </div>

      <!-- Loading Overlay -->
      <v-overlay :value="loading" opacity="0.95">
        <div class="load-wrap">
          <div class="load-spinner"></div>
          <p class="load-text">Initializing FTP monitor<span class="load-dots">...</span></p>
        </div>
      </v-overlay>
    </v-main>
  </div>
</template>

<script>
import Navigation from '@/components/Navigation.vue';
import Vue from 'vue';

export default {
  name: 'FtpDashboard',
  components: { Navigation },
  data() {
    return {
      loading: true,
      refreshing: false,
      ftpServers: [],
      activeFilter: null,
      searchQuery: '',
      viewMode: 'table',
      currentTime: '',
      lastRefresh: null,
      clockInterval: null,
      refreshInterval: null,
    };
  },
  computed: {
    totalCount() { return this.ftpServers.length; },
    onlineCount() { return this.countByStatus('online'); },
    offlineCount() { return this.countByStatus('offline'); },
    errorCount() { return this.countByStatus('error'); },
    onlinePct() { return this.pct(this.onlineCount); },
    offlinePct() { return this.pct(this.offlineCount); },
    errorPct() { return this.pct(this.errorCount); },
    filteredServers() {
      let list = this.ftpServers;
      if (this.activeFilter) {
        list = list.filter(s => s.status.toLowerCase() === this.activeFilter);
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(s =>
          (s.client_name && s.client_name.toLowerCase().includes(q)) ||
          s.site_name.toLowerCase().includes(q) ||
          String(s.id).includes(q) ||
          (s.ftp_host && s.ftp_host.toLowerCase().includes(q))
        );
      }
      return list;
    },
  },
  created() {
    this.fetchServers();
    this.updateClock();
    this.clockInterval = setInterval(this.updateClock, 1000);
    this.refreshInterval = setInterval(this.fetchServers, 30000);
  },
  beforeDestroy() {
    clearInterval(this.clockInterval);
    clearInterval(this.refreshInterval);
  },
  methods: {
    fetchServers() {
      this.refreshing = true;
      Vue.axios.get('/api/ftp-status')
        .then(response => {
          this.ftpServers = Object.entries(response.data).map(([id, data]) => ({
            id: Number(id),
            ...data,
          }));
          this.lastRefresh = this.formatNow();
        })
        .catch(error => {
          console.error('FTP status fetch error:', error);
        })
        .finally(() => {
          this.loading = false;
          this.refreshing = false;
        });
    },
    countByStatus(status) {
      return this.ftpServers.filter(s => s.status.toLowerCase() === status).length;
    },
    pct(count) {
      return this.totalCount ? Math.round((count / this.totalCount) * 100) : 0;
    },
    setFilter(status) {
      this.activeFilter = this.activeFilter === status ? null : status;
    },
    statusLabel(status) {
      const map = { online: 'ONLINE', offline: 'OFFLINE', error: 'ERROR' };
      return map[status.toLowerCase()] || status.toUpperCase();
    },
    updateClock() {
      const d = new Date();
      this.currentTime = d.toLocaleTimeString('en-GB', { hour12: false });
    },
    formatNow() {
      return new Date().toLocaleTimeString('en-GB', { hour12: false });
    },
    relativeTime(dateString) {
      if (!dateString) return '---';
      const now = new Date();
      const date = new Date(dateString.replace(' ', 'T'));
      const seconds = Math.floor((now - date) / 1000);
      if (seconds < 60) return seconds + 's ago';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return minutes + 'm ago';
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return hours + 'h ago';
      const days = Math.floor(hours / 24);
      return days + 'd ago';
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

/* ── Palette ──────────────────────────────────────── */
:root {
  --bg-root: #0b0e14;
  --bg-surface: #11141b;
  --bg-card: #161a23;
  --bg-elevated: #1c2029;
  --border: #232830;
  --border-subtle: #1a1e26;
  --text-primary: #c8cdd5;
  --text-secondary: #6b7280;
  --text-muted: #3f4550;
  --accent: #00d4aa;
  --accent-dim: rgba(0, 212, 170, 0.12);
  --online: #00e676;
  --online-dim: rgba(0, 230, 118, 0.10);
  --offline: #ff9100;
  --offline-dim: rgba(255, 145, 0, 0.10);
  --error: #ff1744;
  --error-dim: rgba(255, 23, 68, 0.10);
}

p { margin: 0 !important; }

/* ── Shell ────────────────────────────────────────── */
.ftp-dash {
  background: var(--bg-root);
  min-height: 100vh;
}

.dash-main {
  background: var(--bg-root) !important;
}

.dash-shell {
  max-width: 1440px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}

/* ── Terminal Header ──────────────────────────────── */
.term-header {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  margin-bottom: 0;
}

.term-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
}

.term-dots {
  display: flex;
  gap: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot--red { background: #ff5f57; }
.dot--yellow { background: #ffbd2e; }
.dot--green { background: #28c840; }

.term-title {
  flex: 1;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--text-secondary);
  text-align: center;
}

.term-clock {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--accent);
  font-weight: 600;
}

.term-clock-icon {
  font-size: 14px;
  color: var(--accent);
}

.term-prompt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
}

.prompt-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.prompt-user { color: var(--accent); font-weight: 700; }
.prompt-at { color: var(--text-muted); }
.prompt-host { color: var(--accent); font-weight: 700; }
.prompt-sep { color: var(--text-muted); margin: 0 2px; }
.prompt-path { color: #5b9bd5; }
.prompt-dollar { color: var(--text-secondary); margin: 0 6px 0 8px; }
.prompt-cmd { color: var(--text-primary); }

.cursor-blink {
  color: var(--accent);
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.prompt-right {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.refresh-indicator {
  display: flex;
  color: var(--text-secondary);
}

.refresh-indicator .material-symbols-outlined {
  font-size: 14px;
}

.refresh-indicator.is-refreshing {
  animation: spin 1s linear infinite;
}

.refresh-label {
  color: var(--online);
}

.refresh-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--online);
  box-shadow: 0 0 6px var(--online);
  animation: pulse-dot 2s ease-in-out infinite;
}

.refresh-dot.is-refreshing {
  background: var(--offline);
  box-shadow: 0 0 6px var(--offline);
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ── Metrics Strip ────────────────────────────────── */
.metrics-strip {
  display: flex;
  align-items: stretch;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.metric {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 14px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}

.metric:hover {
  background: var(--bg-elevated);
}

.metric.active {
  background: var(--bg-elevated);
}

.metric.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: var(--accent);
  border-radius: 2px 2px 0 0;
}

.metric-val {
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.metric-val--online { color: var(--online); }
.metric-val--offline { color: var(--offline); }
.metric-val--error { color: var(--error); }

.metric-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.1em;
  margin-top: 6px;
}

.metric-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  transition: width 0.6s ease;
  opacity: 0.5;
}

.metric-bar--total { width: 100%; background: var(--accent); }
.metric-bar--online { background: var(--online); }
.metric-bar--offline { background: var(--offline); }
.metric-bar--error { background: var(--error); }

.metric-divider {
  width: 1px;
  background: var(--border);
  align-self: stretch;
}

/* ── Controls Row ─────────────────────────────────── */
.controls-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0 12px;
  transition: border-color 0.2s;
}

.search-box:focus-within {
  border-color: var(--accent);
}

.search-icon {
  font-size: 18px;
  color: var(--text-muted);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 10px 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  color: var(--text-primary);
}

.search-input::placeholder {
  color: var(--text-muted);
}

.search-clear {
  font-size: 16px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: color 0.2s;
}

.search-clear:hover {
  color: var(--error);
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ctrl-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.ctrl-btn .material-symbols-outlined {
  font-size: 18px;
  color: var(--text-secondary);
}

.ctrl-btn:hover {
  border-color: var(--accent);
  background: var(--bg-elevated);
}

.ctrl-btn:hover .material-symbols-outlined {
  color: var(--accent);
}

.ctrl-btn.active {
  border-color: var(--accent);
  background: var(--accent-dim);
}

.ctrl-btn.active .material-symbols-outlined {
  color: var(--accent);
}

.ctrl-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ctrl-divider {
  width: 1px;
  height: 24px;
  background: var(--border);
  margin: 0 4px;
}

/* ── Table View ───────────────────────────────────── */
.table-wrap {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.srv-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}

.srv-table thead tr {
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
}

.srv-table th {
  padding: 10px 16px;
  text-align: left;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-muted);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  user-select: none;
}

.srv-row {
  border-bottom: 1px solid var(--border-subtle);
  transition: background 0.15s;
}

.srv-row:last-child {
  border-bottom: none;
}

.srv-row:hover {
  background: var(--bg-elevated);
}

.srv-row td {
  padding: 12px 16px;
  vertical-align: middle;
  color: var(--text-primary);
}

.td-status {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot--online {
  background: var(--online);
  box-shadow: 0 0 8px rgba(0, 230, 118, 0.5);
}

.dot--offline {
  background: var(--offline);
  box-shadow: 0 0 8px rgba(255, 145, 0, 0.5);
  animation: pulse-dot 2s ease-in-out infinite;
}

.dot--error {
  background: var(--error);
  box-shadow: 0 0 8px rgba(255, 23, 68, 0.5);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

.status-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.st--online { color: var(--online); }
.st--offline { color: var(--offline); }
.st--error { color: var(--error); }

.td-id code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--accent);
  background: var(--accent-dim);
  padding: 2px 8px;
  border-radius: 4px;
}

.td-client {
  font-weight: 600;
  color: var(--text-primary);
}

.td-site {
  color: var(--text-secondary);
}

.td-host code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-elevated);
  padding: 2px 8px;
  border-radius: 4px;
}

.td-checked {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 12px;
}

.check-icon {
  font-size: 14px;
  color: var(--text-muted);
}

.row-empty td {
  padding: 0;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  color: var(--text-muted);
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
}

.empty-state .material-symbols-outlined {
  font-size: 24px;
}

/* ── Grid View ────────────────────────────────────── */
.grid-wrap {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.grid-empty {
  grid-column: 1 / -1;
  padding: 48px 16px;
}

.srv-card {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, transform 0.2s;
}

.srv-card:hover {
  border-color: var(--text-muted);
  transform: translateY(-2px);
}

.card--online { border-left: 3px solid var(--online); }
.card--offline { border-left: 3px solid var(--offline); }
.card--error { border-left: 3px solid var(--error); }

.card-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.card-id {
  margin-left: auto;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--accent);
  background: var(--accent-dim);
  padding: 2px 8px;
  border-radius: 4px;
}

.card-body {
  padding: 14px;
}

.card-client {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-site {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-footer {
  padding: 10px 14px;
  border-top: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-host,
.card-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-muted);
}

.card-host .material-symbols-outlined,
.card-time .material-symbols-outlined {
  font-size: 14px;
}

.card-host code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--text-secondary);
}

/* ── Footer ───────────────────────────────────────── */
.dash-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 12px 16px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--text-muted);
}

.footer-left,
.footer-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.footer-left .material-symbols-outlined {
  font-size: 14px;
  color: var(--accent);
}

/* ── Loading ──────────────────────────────────────── */
.load-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.load-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.load-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  color: var(--accent);
  font-weight: 600;
}

.load-dots {
  animation: blink 1s step-end infinite;
}

/* ── Animations ───────────────────────────────────── */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 768px) {
  .dash-shell {
    padding: 12px 12px 32px;
  }

  .term-title {
    display: none;
  }

  .metrics-strip {
    flex-wrap: wrap;
  }

  .metric {
    min-width: 50%;
    flex: unset;
  }

  .metric-divider {
    display: none;
  }

  .metric-val {
    font-size: 22px;
  }

  .controls-row {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-right {
    justify-content: flex-end;
  }

  .srv-table thead {
    display: none;
  }

  .srv-row {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
  }

  .srv-row td {
    padding: 0;
    border: none;
  }

  .td-status {
    width: 100%;
    margin-bottom: 4px;
  }

  .td-id {
    margin-left: auto;
  }

  .td-client {
    width: 100%;
  }

  .td-site {
    width: 100%;
  }

  .td-host {
    width: 100%;
  }

  .td-checked {
    width: 100%;
    margin-top: 4px;
  }

  .dash-footer {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .grid-wrap {
    grid-template-columns: 1fr;
  }

  .prompt-cmd,
  .prompt-right {
    display: none;
  }
}
</style>
